import axios, { AxiosResponse } from 'axios';
import {
  User,
  Job,
  Application,
  Company,
  Skill,
  JobCategory,
  CategoryPreferences,
  CategorySummary,
  RecommendationsResponse,
  EmailPreference,
  EmailPreferencesResponse,
  PrivacyPreference,
  PrivacyPreferencesResponse,
  RecruiterInvitation,
  InvitationsResponse,
  RecruiterProfileBucket,
  NotificationItem,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  ApiResponse,
  PaginatedResponse,
  JobFilters,
  ApplicationFilters,
  DashboardStats,
  CandidateStats,
  RecruiterStats,
  CandidateSearchFilters,
  SavedJobItem,
  RankRequest,
  RankResponse,
  CreateInviteRequest,
  InvitePayload
} from '../types';

const DEFAULT_API =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? `${window.location.origin}/api` : '');

// Create axios instance with base configuration
const api = axios.create({
  baseURL: DEFAULT_API,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const data = error?.response?.data || {};

    if (
      typeof window !== 'undefined' &&
      status === 429 &&
      data?.limit_reached === true &&
      (data?.tier === 'free' || !data?.tier)
    ) {
      window.dispatchEvent(new CustomEvent('pricing-limit-reached', { detail: data }));
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const refreshUrl = `${DEFAULT_API}/auth/refresh`;
          const response = await axios.post(refreshUrl, {}, {
            headers: { Authorization: `Bearer ${refreshToken}` }
          });
          
          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (
    userData: RegisterRequest,
    resumeFile?: File,
  ): Promise<{ message: string; resume_saved?: boolean; resume_error?: string }> => {
    // When a resume is provided we send multipart/form-data so the backend can
    // persist the file as part of the user-create transaction. Otherwise we
    // keep the existing JSON path for backward compatibility.
    if (resumeFile) {
      const formData = new FormData();
      Object.entries(userData).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (typeof value === 'boolean') {
          formData.append(key, value ? 'true' : 'false');
        } else if (typeof value === 'number') {
          formData.append(key, String(value));
        } else if (typeof value === 'string') {
          formData.append(key, value);
        } else {
          // Defensive: serialize anything else as JSON so the backend can
          // parse it if needed. RegisterRequest currently has no nested
          // objects, so this path is unlikely to hit.
          formData.append(key, JSON.stringify(value));
        }
      });
      formData.append('resume', resumeFile);
      const response: AxiosResponse<{ message: string; resume_saved?: boolean; resume_error?: string }> = await api.post(
        '/auth/register',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    }
    const response: AxiosResponse<{ message: string; resume_saved?: boolean; resume_error?: string }> = await api.post('/auth/register', userData);
    return response.data;
  },

  getPartnerProfileInvite: async (token: string): Promise<{ invite: any }> => {
    const response = await api.get(`/auth/partner-profile-invite/${token}`);
    return response.data;
  },

  getAffiliateOffer: async (code: string): Promise<{ offer: any | null }> => {
    const response = await api.get(`/auth/affiliate-offer/${encodeURIComponent(code)}`);
    return response.data;
  },

  refresh: async (): Promise<{ access_token: string; user: User }> => {
    const response = await api.post('/auth/refresh');
    return response.data;
  },

  getCurrentUser: async (): Promise<{ user: User }> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  heartbeat: async (): Promise<{ message: string; heartbeat: any; user?: User }> => {
    const response = await api.post('/auth/heartbeat');
    return response.data;
  },

  changePassword: async (passwords: { current_password: string; new_password: string }): Promise<ApiResponse<void>> => {
    const response = await api.post('/auth/change-password', passwords);
    return response.data;
  },

  forgotPassword: async (payload: { email: string }): Promise<{ message: string }> => {
    const response = await api.post('/auth/forgot-password', payload);
    return response.data;
  },

  verifyResetToken: async (token: string): Promise<{ status: string; message?: string }> => {
    const response = await api.get(`/auth/reset-password/verify/${token}`);
    return response.data;
  },

  resetPassword: async (payload: { token: string; password: string; confirm_password?: string }): Promise<{ message: string }> => {
    const response = await api.post('/auth/reset-password', payload);
    return response.data;
  },

  verifyEmail: async (token: string): Promise<{ status: string; message?: string }> => {
    const response = await api.get(`/auth/verify-email/${token}`);
    return response.data;
  },

  resendVerificationEmail: async (payload: { email: string; new_email?: string }): Promise<{ message: string; email?: string; remaining_attempts?: number; retry_after_seconds?: number | null }> => {
    const response = await api.post('/auth/verify-email/resend', payload);
    return response.data;
  }
};

// Jobs API
export const jobsAPI = {
  getJobs: async (filters: JobFilters = {}): Promise<PaginatedResponse<Job> & { jobs: Job[] }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const query = params.toString();
    const response = await api.get(`/jobs/${query ? `?${query}` : ''}`);
    return response.data;
  },

  getMyJobs: async (filters: JobFilters = {}): Promise<PaginatedResponse<Job> & { jobs: Job[] }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed || (['all'].includes(trimmed.toLowerCase()) && key !== 'status')) {
          return;
        }
        if (key === 'status' && trimmed.toLowerCase() === 'all') {
          return;
        }
        params.append(key, trimmed);
        return;
      }
      params.append(key, value.toString());
    });
    
    const query = params.toString();
    const response = await api.get(`/jobs/my${query ? `?${query}` : ''}`);
    return response.data;
  },

  getJob: async (id: number): Promise<{ job: Job }> => {
    const response = await api.get(`/jobs/${id}`);
    return response.data;
  },

  createJob: async (jobData: Partial<Job>): Promise<{ message: string; job: Job }> => {
    const response = await api.post('/jobs/', jobData);
    return response.data;
  },

  updateJob: async (id: number, jobData: Partial<Job>): Promise<{ message: string; job: Job }> => {
    const response = await api.put(`/jobs/${id}`, jobData);
    return response.data;
  },

  extendJob: async (id: number, payload: { days?: number; expires_at?: string }): Promise<{ message: string; job: Job }> => {
    const response = await api.post(`/jobs/${id}/extend`, payload);
    return response.data;
  },

  deleteJob: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/jobs/${id}`);
    return response.data;
  },

  getJobApplications: async (jobId: number, filters: ApplicationFilters = {}): Promise<PaginatedResponse<Application> & { applications: Application[]; job: Job }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await api.get(`/jobs/${jobId}/applications?${params.toString()}`);
    return response.data;
  },

  getJobStats: async (): Promise<{ stats: any }> => {
    const response = await api.get('/jobs/stats');
    return response.data;
  },

  getPublicStats: async (): Promise<{ stats: { active_jobs: number; total_candidates: number; avg_match_score: number; avg_time_to_shortlist_days: number } }> => {
    const response = await api.get('/jobs/public-stats');
    return response.data;
  },

  getSavedJobs: async (params: { page?: number; per_page?: number } = {}): Promise<PaginatedResponse<SavedJobItem> & { saved_jobs: SavedJobItem[] }> => {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.per_page) query.append('per_page', String(params.per_page));
    const response = await api.get(`/jobs/saved${query.toString() ? `?${query.toString()}` : ''}`);
    return response.data;
  },

  getMyMatch: async (jobId: number): Promise<{ match_score: number; ai_analysis: any | null }> => {
    const response = await api.get(`/jobs/${jobId}/my-match`);
    return response.data;
  },

  saveJob: async (jobId: number): Promise<{ message: string; saved_job: SavedJobItem }> => {
    const response = await api.post(`/jobs/${jobId}/save`);
    return response.data;
  },

  unsaveJob: async (jobId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/jobs/${jobId}/save`);
    return response.data;
  },

  parseSkills: async (description: string, requirements: string): Promise<{
    suggested: Array<{
      skill_id: number;
      skill_name: string;
      matched_via: 'name' | 'alias';
      matched_alias: string | null;
    }>;
  }> => {
    const response = await api.post('/jobs/parse-skills', { description, requirements });
    return response.data;
  },
};

// Applications API
export const applicationsAPI = {
  createApplication: async (applicationData: { job_id: number; cover_letter?: string; cover_letter_file?: File | null }): Promise<{ message: string; application: Application }> => {
    if (applicationData.cover_letter_file instanceof File) {
      const formData = new FormData();
      formData.append('job_id', applicationData.job_id.toString());
      if (applicationData.cover_letter) {
        formData.append('cover_letter', applicationData.cover_letter);
      }
      formData.append('cover_letter_file', applicationData.cover_letter_file);
      const response = await api.post('/applications/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    }
    const response = await api.post('/applications/', {
      job_id: applicationData.job_id,
      cover_letter: applicationData.cover_letter
    });
    return response.data;
  },

  getApplications: async (filters: ApplicationFilters = {}): Promise<PaginatedResponse<Application> & { applications: Application[] }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await api.get(`/applications/?${params.toString()}`);
    return response.data;
  },

  getApplication: async (id: number): Promise<{ application: Application }> => {
    const response = await api.get(`/applications/${id}`);
    return response.data;
  },

  updateApplicationStatus: async (id: number, status: string, notes?: string): Promise<{ message: string; application: Application }> => {
    const response = await api.put(`/applications/${id}/status`, { status, notes });
    return response.data;
  },

  withdrawApplication: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/applications/${id}`);
    return response.data;
  },

  getApplicationHistory: async (id: number): Promise<{ application_id: number; history: any[] }> => {
    const response = await api.get(`/applications/${id}/history`);
    return response.data;
  },

  downloadApplicationResume: async (applicationId: number): Promise<Blob> => {
    const response = await api.get(`/applications/${applicationId}/resume`, { responseType: 'blob' });
    return response.data;
  },

  downloadApplicationCoverLetter: async (applicationId: number): Promise<Blob> => {
    const response = await api.get(`/applications/${applicationId}/cover-letter`, { responseType: 'blob' });
    return response.data;
  },

  getApplicationStats: async (): Promise<{ stats: CandidateStats | RecruiterStats | any }> => {
    const response = await api.get('/applications/stats');
    return response.data;
  }
};

// Users API
export const usersAPI = {
  getProfile: async (): Promise<{ profile: User }> => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  updateProfile: async (profileData: Partial<User>): Promise<{ message: string; profile: User }> => {
    const response = await api.put('/users/profile', profileData);
    return response.data;
  },

  getCandidateProfileForRecruiter: async (candidateId: number): Promise<{ candidate: User }> => {
    const response = await api.get(`/users/candidates/${candidateId}`);
    return response.data;
  },

  downloadCandidateResume: async (candidateId: number): Promise<Blob> => {
    const endpoint = candidateId < 0
      ? `/users/partner-profiles/${Math.abs(candidateId)}/resume`
      : `/uploads/resume/${candidateId}`;
    const response = await api.get(endpoint, { responseType: 'blob' });
    return response.data;
  },

  searchCandidatesForRecruiter: async (filters: CandidateSearchFilters = {}): Promise<PaginatedResponse<User> & { candidates: User[] }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        if (value.length > 0) {
          params.append(key, value.join(','));
        }
        return;
      }
      const trimmed = typeof value === 'string' ? value.trim() : value;
      if (trimmed !== '' && trimmed !== null) {
        params.append(key, trimmed.toString());
      }
    });

    const response = await api.get(`/users/candidates/search${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  },

  listRecruiterBuckets: async (): Promise<{ buckets: RecruiterProfileBucket[] }> => {
    const response = await api.get('/users/recruiter-buckets');
    return response.data;
  },

  createRecruiterBucket: async (name: string): Promise<{ message: string; bucket: RecruiterProfileBucket }> => {
    const response = await api.post('/users/recruiter-buckets', { name });
    return response.data;
  },

  saveCandidateToRecruiterBucket: async (payload: { candidate_id: number; bucket_id?: number; new_bucket_name?: string; application_id?: number; source?: string }): Promise<{ message: string; bucket: RecruiterProfileBucket; profile: any }> => {
    const response = await api.post('/users/recruiter-buckets/save-profile', payload);
    return response.data;
  },

  listRecruiterBucketProfiles: async (bucketId: number): Promise<{ bucket: RecruiterProfileBucket; profiles: any[] }> => {
    const response = await api.get(`/users/recruiter-buckets/${bucketId}/profiles`);
    return response.data;
  },

  getRecruiterBucketProfileDetail: async (bucketId: number, candidateId: number): Promise<{ bucket: RecruiterProfileBucket; latest_version: any; versions: any[]; snapshot: any; resume_download_url?: string | null }> => {
    const response = await api.get(`/users/recruiter-buckets/${bucketId}/profiles/${candidateId}`);
    return response.data;
  },

  refreshRecruiterBucketProfile: async (bucketId: number, candidateId: number): Promise<{ message: string; profile: any }> => {
    const response = await api.post(`/users/recruiter-buckets/${bucketId}/profiles/${candidateId}/refresh`);
    return response.data;
  },

  getRecruiterBucketResumeUrl: async (profileVersionId: number): Promise<{ download_url: string; expires_in: number }> => {
    const response = await api.get(`/users/recruiter-buckets/profiles/${profileVersionId}/resume`);
    return response.data;
  },

  downloadRecruiterBucketResumeFile: async (profileVersionId: number): Promise<Blob> => {
    const response = await api.get(`/users/recruiter-buckets/profiles/${profileVersionId}/resume?mode=file`, {
      responseType: 'blob'
    });
    return response.data;
  },

  addEducation: async (educationData: any): Promise<{ message: string; education: any }> => {
    const response = await api.post('/users/education', educationData);
    return response.data;
  },

  updateEducation: async (educationId: number, educationData: any): Promise<{ message: string; education: any }> => {
    const response = await api.put(`/users/education/${educationId}`, educationData);
    return response.data;
  },

  deleteEducation: async (educationId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/users/education/${educationId}`);
    return response.data;
  },

  addExperience: async (experienceData: any): Promise<{ message: string; experience: any }> => {
    const response = await api.post('/users/experience', experienceData);
    return response.data;
  },

  updateExperience: async (experienceId: number, experienceData: any): Promise<{ message: string; experience: any }> => {
    const response = await api.put(`/users/experience/${experienceId}`, experienceData);
    return response.data;
  },

  deleteExperience: async (experienceId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/users/experience/${experienceId}`);
    return response.data;
  },

  addCertification: async (certificationData: any): Promise<{ message: string; certification: any }> => {
    const response = await api.post('/users/certifications', certificationData);
    return response.data;
  },

  updateCertification: async (certificationId: number, certificationData: any): Promise<{ message: string; certification: any }> => {
    const response = await api.put(`/users/certifications/${certificationId}`, certificationData);
    return response.data;
  },

  deleteCertification: async (certificationId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/users/certifications/${certificationId}`);
    return response.data;
  },

  getAllSkills: async (): Promise<{ skills: { [category: string]: Skill[] } }> => {
    const response = await api.get('/users/skills');
    return response.data;
  },

  addSkill: async (skillData: { skill_id?: number; skill_name?: string; proficiency_level: number; years_experience?: number }): Promise<{ message: string; skill: any }> => {
    const response = await api.post('/users/skills', skillData);
    return response.data;
  },

  updateSkill: async (skillId: number, skillData: { proficiency_level?: number; years_experience?: number }): Promise<{ message: string; skill: any }> => {
    const response = await api.put(`/users/skills/${skillId}`, skillData);
    return response.data;
  },

  deleteSkill: async (skillId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/users/skills/${skillId}`);
    return response.data;
  },

  getUsers: async (filters: any = {}): Promise<PaginatedResponse<User> & { users: User[] }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await api.get(`/users/?${params.toString()}`);
    return response.data;
  }
};

// Companies API
export const companiesAPI = {
  getCompanies: async (filters: any = {}): Promise<PaginatedResponse<Company> & { companies: Company[] }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await api.get(`/companies/?${params.toString()}`);
    return response.data;
  },

  getCompany: async (id: number): Promise<{ company: Company }> => {
    const response = await api.get(`/companies/${id}`);
    return response.data;
  },

  createCompany: async (companyData: Partial<Company>): Promise<{ message: string; company: Company }> => {
    const response = await api.post('/companies/', companyData);
    return response.data;
  },

  updateCompany: async (id: number, companyData: Partial<Company>): Promise<{ message: string; company: Company }> => {
    const response = await api.put(`/companies/${id}`, companyData);
    return response.data;
  },

  deleteCompany: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/companies/${id}`);
    return response.data;
  },

  getCompanyJobs: async (companyId: number, filters: any = {}): Promise<PaginatedResponse<Job> & { jobs: Job[]; company: Company }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/companies/${companyId}/jobs?${params.toString()}`);
    return response.data;
  }
};

// Job Categories API
export const jobCategoriesAPI = {
  getTree: async (
    opts?: { withCounts?: boolean }
  ): Promise<{ categories: JobCategory[] }> => {
    const params = opts?.withCounts ? '?with_counts=1' : '';
    const response = await api.get(`/job-categories${params}`);
    return response.data;
  },

  getBySlug: async (slug: string): Promise<{ category: JobCategory }> => {
    const response = await api.get(`/job-categories/${encodeURIComponent(slug)}`);
    return response.data;
  },

  getSummary: async (
    parent: string,
    child?: string
  ): Promise<CategorySummary> => {
    const path = child
      ? `/job-categories/${encodeURIComponent(parent)}/${encodeURIComponent(child)}/summary`
      : `/job-categories/${encodeURIComponent(parent)}/summary`;
    const response = await api.get(path);
    return response.data;
  },

  search: async (q: string, level?: 'parent' | 'child', parentId?: number): Promise<{
    items: Array<{ id: number; name: string; level: 'parent' | 'child'; parent_id?: number | null; matched_via: 'name' | 'alias' }>;
  }> => {
    const params: any = { q };
    if (level) params.level = level;
    if (parentId != null) params.parent_id = parentId;
    const response = await api.get('/job-categories', { params });
    return response.data;
  },
};

// Admin Job Categories API
export interface AdminJobCategoryRow {
  id: number;
  name: string;
  slug: string;
  parent_id: number | null;
  description?: string | null;
  display_order: number;
  is_active: boolean;
  active_job_count: number;
  parent?: { id: number; name: string; slug: string };
}

export const adminJobCategoriesAPI = {
  getAll: async (
    opts?: { includeInactive?: boolean }
  ): Promise<{ categories: AdminJobCategoryRow[] }> => {
    const params = opts?.includeInactive ? '?include_inactive=1' : '';
    const response = await api.get(`/admin/job-categories${params}`);
    return response.data;
  },

  create: async (payload: {
    name: string;
    parent_id: number | null;
    description?: string;
    display_order?: number;
  }): Promise<{ category: JobCategory }> => {
    const response = await api.post('/admin/job-categories', payload);
    return response.data;
  },

  update: async (
    id: number,
    payload: Partial<{ name: string; description: string; display_order: number; is_active: boolean }>
  ): Promise<{ category: JobCategory }> => {
    const response = await api.patch(`/admin/job-categories/${id}`, payload);
    return response.data;
  },

  reorder: async (
    rows: Array<{ id: number; display_order: number }>
  ): Promise<{ updated: number }> => {
    const response = await api.patch('/admin/job-categories/reorder', rows);
    return response.data;
  },

  deactivate: async (id: number): Promise<{ category: JobCategory }> => {
    const response = await api.patch(`/admin/job-categories/${id}`, { is_active: false });
    return response.data;
  },

  activate: async (id: number): Promise<{ category: JobCategory }> => {
    const response = await api.patch(`/admin/job-categories/${id}`, { is_active: true });
    return response.data;
  },

  getJobCount: async (id: number): Promise<{ active_job_count: number }> => {
    const response = await api.get(`/admin/job-categories/${id}/job-count`);
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getDashboard: async (): Promise<{ dashboard: DashboardStats }> => {
    const response = await api.get('/admin/dashboard');
    return response.data;
  },

  getAllUsers: async (filters: any = {}): Promise<PaginatedResponse<User> & { users: User[] }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await api.get(`/admin/users?${params.toString()}`);
    return response.data;
  },

  toggleUserStatus: async (userId: number): Promise<{ message: string; user: User }> => {
    const response = await api.post(`/admin/users/${userId}/toggle-status`);
    return response.data;
  },

  getAllJobs: async (filters: any = {}): Promise<PaginatedResponse<Job> & { jobs: Job[] }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await api.get(`/admin/jobs?${params.toString()}`);
    return response.data;
  },

  getSettings: async (): Promise<{ settings: Record<string, number>; defaults: Record<string, number> }> => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  updateSettings: async (settings: Record<string, number>): Promise<{ message: string; settings: Record<string, number> }> => {
    const response = await api.put('/admin/settings', { settings });
    return response.data;
  },

  listBillingCoupons: async (params: { page?: number; per_page?: number } = {}): Promise<{ coupons: any[]; total: number; pages: number; current_page: number; per_page: number; has_next: boolean; has_prev: boolean }> => {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', params.page.toString());
    if (params.per_page) qs.append('per_page', params.per_page.toString());
    const response = await api.get(`/admin/billing/coupons${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  createBillingCoupon: async (payload: any): Promise<{ coupon: any }> => {
    const response = await api.post('/admin/billing/coupons', payload);
    return response.data;
  },

  updateBillingCoupon: async (couponId: number, payload: any): Promise<{ coupon: any }> => {
    const response = await api.put(`/admin/billing/coupons/${couponId}`, payload);
    return response.data;
  },

  deleteBillingCoupon: async (couponId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/billing/coupons/${couponId}`);
    return response.data;
  },

  getBillingCoupon: async (couponId: number): Promise<{ coupon: any; components: any[]; invites: any[]; users: any[] }> => {
    const response = await api.get(`/admin/billing/coupons/${couponId}`);
    return response.data;
  },

  listBillingInvites: async (couponId: number): Promise<{ invites: any[] }> => {
    const response = await api.get(`/admin/billing/coupons/${couponId}/invites`);
    return response.data;
  },

  createBillingInvite: async (couponId: number, payload: any): Promise<{ invite: any; invite_link: string }> => {
    const response = await api.post(`/admin/billing/coupons/${couponId}/invites`, payload);
    return response.data;
  },

  listBillingComponents: async (): Promise<{ components: any[] }> => {
    const response = await api.get('/admin/billing/components');
    return response.data;
  },

  createBillingComponent: async (payload: any): Promise<{ component: any }> => {
    const response = await api.post('/admin/billing/components', payload);
    return response.data;
  },

  updateBillingComponent: async (componentId: number, payload: any): Promise<{ component: any }> => {
    const response = await api.put(`/admin/billing/components/${componentId}`, payload);
    return response.data;
  },

  deleteBillingComponent: async (componentId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/billing/components/${componentId}`);
    return response.data;
  },

  assignBillingComponent: async (couponId: number, componentId: number): Promise<{ assignment: any }> => {
    const response = await api.post(`/admin/billing/coupons/${couponId}/components`, { component_id: componentId });
    return response.data;
  },

  removeBillingComponent: async (couponId: number, componentId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/billing/coupons/${couponId}/components/${componentId}`);
    return response.data;
  },

  updateBillingAssignment: async (couponId: number, componentId: number, payload: any): Promise<{ assignment: any }> => {
    const response = await api.put(`/admin/billing/coupons/${couponId}/components/${componentId}`, payload);
    return response.data;
  },

  listPartnerUploads: async (params: { page?: number; per_page?: number } = {}): Promise<{ uploads: any[]; total: number; pages: number; current_page: number; per_page: number; has_next: boolean; has_prev: boolean }> => {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', params.page.toString());
    if (params.per_page) qs.append('per_page', params.per_page.toString());
    const response = await api.get(`/admin/billing/partner-uploads${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  listPartnerUploadIds: async (params: { partner?: string; type?: string; status?: string; component?: string } = {}): Promise<{ upload_ids: number[] }> => {
    const qs = new URLSearchParams();
    if (params.partner) qs.append('partner', params.partner);
    if (params.type) qs.append('type', params.type);
    if (params.status) qs.append('status', params.status);
    if (params.component) qs.append('component', params.component);
    const response = await api.get(`/admin/billing/partner-uploads/ids${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  getPartnerUpload: async (uploadId: number): Promise<{ upload: any; profiles: any[] }> => {
    const response = await api.get(`/admin/billing/partner-uploads/${uploadId}`);
    return response.data;
  },

  updatePartnerUploadStatus: async (uploadId: number, payload: { status: 'pending' | 'approved' | 'rejected'; notes?: string }): Promise<{ upload: any }> => {
    const response = await api.post(`/admin/billing/partner-uploads/${uploadId}/status`, payload);
    return response.data;
  },

  updatePartnerProfileStatus: async (profileId: number, payload: { status: 'pending' | 'approved' | 'rejected'; notes?: string }): Promise<{ profile: any }> => {
    const response = await api.post(`/admin/billing/partner-profiles/${profileId}/status`, payload);
    return response.data;
  },

  downloadPartnerProfileResume: async (profileId: number): Promise<Blob> => {
    const response = await api.get(`/admin/billing/partner-profiles/${profileId}/resume`, { responseType: 'blob' });
    return response.data;
  },

  listPartnerProfiles: async (params: { status?: string; page?: number; per_page?: number } = {}): Promise<{ profiles: any[]; total: number; pages: number; current_page: number; per_page: number; has_next: boolean; has_prev: boolean }> => {
    const qs = new URLSearchParams();
    if (params.status) qs.append('status', params.status);
    if (params.page) qs.append('page', params.page.toString());
    if (params.per_page) qs.append('per_page', params.per_page.toString());
    const response = await api.get(`/admin/billing/partner-profiles${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  listKycReviews: async (params: { status?: string; search?: string; page?: number; per_page?: number } = {}): Promise<{ reviews: any[]; total: number; pages: number; current_page: number; per_page: number; has_next: boolean; has_prev: boolean }> => {
    const qs = new URLSearchParams();
    if (params.status) qs.append('status', params.status);
    if (params.search) qs.append('search', params.search);
    if (params.page) qs.append('page', params.page.toString());
    if (params.per_page) qs.append('per_page', params.per_page.toString());
    const response = await api.get(`/admin/billing/kyc-reviews${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  getKycReview: async (partnerId: number): Promise<{ partner: any; kyc: any }> => {
    const response = await api.get(`/admin/billing/kyc-reviews/${partnerId}`);
    return response.data;
  },

  updateKycReviewStatus: async (partnerId: number, payload: { status: 'pending' | 'approved' | 'rejected'; notes?: string }): Promise<{ partner: any; kyc: any }> => {
    const response = await api.post(`/admin/billing/kyc-reviews/${partnerId}/status`, payload);
    return response.data;
  },

  downloadKycDocument: async (documentId: number): Promise<Blob> => {
    const response = await api.get(`/admin/billing/kyc-documents/${documentId}/download`, { responseType: 'blob' });
    return response.data;
  },

  listAffiliateReferrals: async (params: { status?: string; partner?: string; candidate?: string; page?: number; per_page?: number } = {}): Promise<{ referrals: any[]; total: number; pages: number; current_page: number; per_page: number; has_next: boolean; has_prev: boolean }> => {
    const qs = new URLSearchParams();
    if (params.status) qs.append('status', params.status);
    if (params.partner) qs.append('partner', params.partner);
    if (params.candidate) qs.append('candidate', params.candidate);
    if (params.page) qs.append('page', params.page.toString());
    if (params.per_page) qs.append('per_page', params.per_page.toString());
    const response = await api.get(`/admin/billing/affiliate-referrals${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  updateAffiliateReferralStatus: async (referralId: number, payload: { status: 'pending' | 'approved' | 'rejected'; notes?: string }): Promise<{ referral: any }> => {
    const response = await api.post(`/admin/billing/affiliate-referrals/${referralId}/status`, payload);
    return response.data;
  },

  listSupportTickets: async (params: { status?: string; category?: string; published?: string; search?: string; page?: number; per_page?: number } = {}): Promise<{ tickets: any[]; total: number; pages: number; current_page: number; per_page: number; has_next: boolean; has_prev: boolean }> => {
    const qs = new URLSearchParams();
    if (params.status) qs.append('status', params.status);
    if (params.category) qs.append('category', params.category);
    if (params.published) qs.append('published', params.published);
    if (params.search) qs.append('search', params.search);
    if (params.page) qs.append('page', params.page.toString());
    if (params.per_page) qs.append('per_page', params.per_page.toString());
    const response = await api.get(`/admin/support/tickets${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  closeSupportTicket: async (ticketId: number, payload: { admin_response?: string } = {}): Promise<{ ticket: any }> => {
    const response = await api.post(`/admin/support/tickets/${ticketId}/close`, payload);
    return response.data;
  },

  publishSupportTicket: async (ticketId: number, payload: { publish?: boolean; admin_response?: string } = {}): Promise<{ ticket: any }> => {
    const response = await api.post(`/admin/support/tickets/${ticketId}/publish`, payload);
    return response.data;
  },

  assignSupportTicket: async (ticketId: number, payload: { assigned_admin_id?: number | null }): Promise<{ ticket: any }> => {
    const response = await api.post(`/admin/support/tickets/${ticketId}/assign`, payload);
    return response.data;
  },

  listSupportAdmins: async (): Promise<{ admins: Array<{ id: number; full_name: string; email: string }> }> => {
    const response = await api.get('/admin/support/admins');
    return response.data;
  },

  downloadSupportAttachment: async (ticketId: number, attachmentId: number): Promise<Blob> => {
    const response = await api.get(`/support/tickets/${ticketId}/attachments/${attachmentId}/download`, { responseType: 'blob' });
    return response.data;
  },

  exportPartnerUploads: async (params: { upload_ids?: number[] } = {}): Promise<{ blob: Blob; filename: string }> => {
    const qs = new URLSearchParams();
    if (params.upload_ids && params.upload_ids.length > 0) {
      qs.append('upload_ids', params.upload_ids.join(','));
    }
    const response = await api.get(`/admin/billing/partner-uploads/export${qs.toString() ? `?${qs.toString()}` : ''}`, { responseType: 'blob' });
    const disposition = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `partner_uploads_${new Date().toISOString().replace(/[-:]/g, '').replace('T', '_').slice(0, 15)}.zip`;
    return { blob: response.data, filename };
  },

  listBillingPartners: async (params: { page?: number; per_page?: number; search?: string } = {}): Promise<{ partners: any[]; total: number; pages: number; current_page: number; per_page: number; has_next: boolean; has_prev: boolean }> => {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', params.page.toString());
    if (params.per_page) qs.append('per_page', params.per_page.toString());
    if (params.search) qs.append('search', params.search);
    const response = await api.get(`/admin/billing/partners${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  getBillingPartner: async (partnerId: number): Promise<{ partner: any }> => {
    const response = await api.get(`/admin/billing/partners/${partnerId}`);
    return response.data;
  },

  updateBillingPartnerStatus: async (partnerId: number, payload: { is_active: boolean }): Promise<{ partner: any }> => {
    const response = await api.post(`/admin/billing/partners/${partnerId}/status`, payload);
    return response.data;
  },

  getBillingDashboard: async (scale: 'day' | 'month' | 'year' = 'month', params: { start_date?: string; end_date?: string } = {}): Promise<any> => {
    const qs = new URLSearchParams();
    qs.append('scale', scale);
    if (params.start_date) qs.append('start_date', params.start_date);
    if (params.end_date) qs.append('end_date', params.end_date);
    const response = await api.get(`/admin/billing/dashboard?${qs.toString()}`);
    return response.data;
  },

  listBillingPaymentLinks: async (): Promise<{ links: any[] }> => {
    const response = await api.get('/admin/billing/payment-links');
    return response.data;
  },

  upsertBillingPaymentLink: async (
    planKey: 'candidate_silver' | 'recruiter_silver',
    payload: { plan_name?: string; amount?: number; currency?: string; payment_link_url: string; is_live?: boolean; notes?: string }
  ): Promise<{ link: any }> => {
    const response = await api.put(`/admin/billing/payment-links/${planKey}`, payload);
    return response.data;
  },

  disableBillingPaymentLink: async (
    planKey: 'candidate_silver' | 'recruiter_silver'
  ): Promise<{ message: string; link: any }> => {
    const response = await api.delete(`/admin/billing/payment-links/${planKey}`);
    return response.data;
  },

  listBillingPaymentTransactions: async (params: { status?: string; plan_key?: string; search?: string; page?: number; per_page?: number } = {}): Promise<{
    transactions: any[];
    summary: { success: number; failed: number; pending_or_other: number };
    total: number;
    pages: number;
    current_page: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  }> => {
    const qs = new URLSearchParams();
    if (params.status) qs.append('status', params.status);
    if (params.plan_key) qs.append('plan_key', params.plan_key);
    if (params.search) qs.append('search', params.search);
    if (params.page) qs.append('page', params.page.toString());
    if (params.per_page) qs.append('per_page', params.per_page.toString());
    const response = await api.get(`/admin/billing/payment-transactions${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  exportBillingPaymentTransactions: async (params: { start_date: string; end_date: string; status?: string; plan_key?: string; search?: string }):
    Promise<{ blob: Blob; filename: string }> => {
    const qs = new URLSearchParams();
    qs.append('start_date', params.start_date);
    qs.append('end_date', params.end_date);
    if (params.status) qs.append('status', params.status);
    if (params.plan_key) qs.append('plan_key', params.plan_key);
    if (params.search) qs.append('search', params.search);
    const response = await api.get(`/admin/billing/payment-transactions/export?${qs.toString()}`, { responseType: 'blob' });
    const disposition = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `payment_transactions_${new Date().toISOString().slice(0, 10)}.csv`;
    return { blob: response.data, filename };
  },

  markBillingPaymentTransactionPaid: async (transactionId: number): Promise<{ message: string; transaction: any }> => {
    const response = await api.post(`/admin/billing/payment-transactions/${transactionId}/mark-paid`);
    return response.data;
  },

  reconcileStuckBillingPayments: async (params: { older_than_minutes?: number; max_age_days?: number; limit?: number } = {}): Promise<{
    result: { checked: number; updated_success: number; updated_failed: number; errors: string[] };
  }> => {
    const response = await api.post('/admin/billing/payment-transactions/reconcile-stuck', params);
    return response.data;
  },

  getPricingConfig: async (): Promise<{ config: Record<string, number> }> => {
    const response = await api.get('/admin/billing/pricing-config');
    return response.data;
  },

  updatePricingConfig: async (
    config: Record<string, number>,
    confirmChanges: boolean
  ): Promise<{ message: string; config: Record<string, number> }> => {
    const response = await api.put('/admin/billing/pricing-config', {
      config,
      confirm_changes: confirmChanges
    });
    return response.data;
  },

  searchRecruitersForExceptions: async (query: string): Promise<{ recruiters: any[] }> => {
    const qs = new URLSearchParams();
    qs.append('query', query);
    const response = await api.get(`/admin/billing/recruiter-exceptions/search?${qs.toString()}`);
    return response.data;
  },

  listRecruiterExceptions: async (params: { page?: number; per_page?: number; search?: string } = {}): Promise<{
    exceptions: any[];
    total: number;
    pages: number;
    current_page: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  }> => {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', String(params.page));
    if (params.per_page) qs.append('per_page', String(params.per_page));
    if (params.search) qs.append('search', params.search);
    const response = await api.get(`/admin/billing/recruiter-exceptions${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  getRecruiterExceptionLimits: async (recruiterId: number): Promise<{
    recruiter: any;
    effective_limits: any;
    exception_limits: any;
    in_exception_list: boolean;
  }> => {
    const response = await api.get(`/admin/billing/recruiter-exceptions/${recruiterId}/limits`);
    return response.data;
  },

  upsertRecruiterException: async (recruiterId: number, limits: {
    job_postings_monthly?: number | null;
    searches_monthly?: number | null;
    talent_views_monthly?: number | null;
    buckets_total?: number | null;
  }): Promise<{ message: string; exception: any }> => {
    const response = await api.put(`/admin/billing/recruiter-exceptions/${recruiterId}`, { limits });
    return response.data;
  },

  removeRecruiterException: async (recruiterId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/billing/recruiter-exceptions/${recruiterId}`);
    return response.data;
  },

  getBillingInvoice: async (params: { partner_id: number; start_date: string; end_date: string }): Promise<any> => {
    const qs = new URLSearchParams();
    qs.append('partner_id', params.partner_id.toString());
    qs.append('start_date', params.start_date);
    qs.append('end_date', params.end_date);
    const response = await api.get(`/admin/billing/invoices?${qs.toString()}`);
    return response.data;
  },

  downloadBillingInvoice: async (params: { partner_id: number; start_date: string; end_date: string }): Promise<{ blob: Blob; filename: string }> => {
    const qs = new URLSearchParams();
    qs.append('partner_id', params.partner_id.toString());
    qs.append('start_date', params.start_date);
    qs.append('end_date', params.end_date);
    qs.append('format', 'csv');
    const response = await api.get(`/admin/billing/invoices?${qs.toString()}`, { responseType: 'blob' });
    const disposition = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `invoice_${params.partner_id}_${params.start_date}_${params.end_date}.csv`;
    return { blob: response.data, filename };
  },

  getPartnerDashboard: async (scale: 'day' | 'month' | 'year' = 'month'): Promise<any> => {
    const response = await api.get(`/partner/dashboard?scale=${scale}`);
    return response.data;
  },

  getAllSkills: async (): Promise<{ skills: { [category: string]: Skill[] } }> => {
    const response = await api.get('/admin/skills');
    return response.data;
  },

  createSkill: async (skillData: { name: string; category?: string }): Promise<{ message: string; skill: Skill }> => {
    const response = await api.post('/admin/skills', skillData);
    return response.data;
  },

  getJdReviews: async (status?: string): Promise<{ jobs: any[] }> => {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await api.get(`/admin/jd-reviews${params}`);
    return response.data;
  },

  getJdReview: async (jobId: number): Promise<{ job: Job & { recruiter?: User } }> => {
    const response = await api.get(`/admin/jd-reviews/${jobId}`);
    return response.data;
  },

  submitJdDecision: async (jobId: number, payload: { decision: 'approve' | 'block'; notes?: string }): Promise<{ message: string; job: Job }> => {
    const response = await api.post(`/admin/jd-reviews/${jobId}/decision`, payload);
    return response.data;
  },

  getJdReviewStats: async (scale: 'day' | 'month' | 'year'): Promise<{
    pending_total: number;
    review_total: number;
    completed_total: number;
    processed_series: Array<{ key: string; label: string; hint: string; count: number }>;
    queue_series: Array<{ key: string; label: string; hint: string; count: number }>;
  }> => {
    const response = await api.get(`/admin/jd-review/stats?scale=${scale}`);
    return response.data;
  },

  getAnalytics: async (days: number = 30): Promise<{ analytics: any }> => {
    const response = await api.get(`/admin/analytics?days=${days}`);
    return response.data;
  },

  getActivityOverview: async (params: { active_window_minutes?: number; scale?: 'day' | 'week' | 'month' | 'year'; date_from?: string; date_to?: string; month?: string } = {}): Promise<any> => {
    const qs = new URLSearchParams();
    if (params.active_window_minutes) qs.append('active_window_minutes', params.active_window_minutes.toString());
    if (params.scale) qs.append('scale', params.scale);
    if (params.date_from) qs.append('date_from', params.date_from);
    if (params.date_to) qs.append('date_to', params.date_to);
    if (params.month) qs.append('month', params.month);
    const response = await api.get(`/admin/activity/overview${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  listActivityVisits: async (params: { scale?: 'day' | 'week' | 'month' | 'year'; status?: 'all' | 'success' | 'failed'; search?: string; page?: number; per_page?: number; active_window_minutes?: number; date_from?: string; date_to?: string; month?: string } = {}): Promise<{ visits: any[]; total: number; pages: number; current_page: number; per_page: number; has_next: boolean; has_prev: boolean; scale: string; status: string; active_window_minutes: number }> => {
    const qs = new URLSearchParams();
    if (params.scale) qs.append('scale', params.scale);
    if (params.status) qs.append('status', params.status);
    if (params.search) qs.append('search', params.search);
    if (params.page) qs.append('page', params.page.toString());
    if (params.per_page) qs.append('per_page', params.per_page.toString());
    if (params.active_window_minutes) qs.append('active_window_minutes', params.active_window_minutes.toString());
    if (params.date_from) qs.append('date_from', params.date_from);
    if (params.date_to) qs.append('date_to', params.date_to);
    if (params.month) qs.append('month', params.month);
    const response = await api.get(`/admin/activity/visits${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  }
};

export const partnerAPI = {
  getDashboard: async (scale: 'day' | 'month' | 'year' = 'month'): Promise<any> => {
    const response = await api.get(`/partner/dashboard?scale=${scale}`);
    return response.data;
  },

  listComponents: async (): Promise<{ components: any[] }> => {
    const response = await api.get('/partner/components');
    return response.data;
  },

  getKycStatus: async (): Promise<{ kyc: any }> => {
    const response = await api.get('/partner/kyc');
    return response.data;
  },

  submitKycDocuments: async (payload: FormData): Promise<{ message: string; kyc: any }> => {
    const response = await api.post('/partner/kyc', payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  listAffiliateLinks: async (): Promise<{ affiliate_links: any[] }> => {
    const response = await api.get('/partner/affiliate-links');
    return response.data;
  },

  createAffiliateLink: async (payload: { channel: 'linkedin' | 'whatsapp' | 'facebook' | 'email'; campaign_name?: string; component_id: number }): Promise<{ affiliate_link: any }> => {
    const response = await api.post('/partner/affiliate-links', payload);
    return response.data;
  },

  updateAffiliateLink: async (linkId: number, payload: { is_active?: boolean; campaign_name?: string; component_id?: number }): Promise<{ affiliate_link: any }> => {
    const response = await api.patch(`/partner/affiliate-links/${linkId}`, payload);
    return response.data;
  },

  listAffiliateReferrals: async (params: { status?: string } = {}): Promise<{ referrals: any[] }> => {
    const qs = new URLSearchParams();
    if (params.status) qs.append('status', params.status);
    const response = await api.get(`/partner/affiliate-referrals${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },
};

export const settingsAPI = {
  getPublicSettings: async (): Promise<{ settings: Record<string, number> }> => {
    const response = await api.get('/settings/public');
    return response.data;
  }
};

export const paymentsAPI = {
  listLiveLinks: async (): Promise<{ links: any[] }> => {
    const response = await api.get('/payments/links');
    return response.data;
  },
  getAffiliateOffer: async (): Promise<{ offer: any | null }> => {
    const response = await api.get('/payments/affiliate-offer');
    return response.data;
  },
  initiatePayment: async (plan_key: string): Promise<{ message: string; payment_url: string; transaction: any }> => {
    const response = await api.post('/payments/initiate', { plan_key });
    return response.data;
  },
  initiateAffiliatePayment: async (): Promise<{ message: string; payment_url: string; transaction: any; affiliate_offer: any }> => {
    const response = await api.post('/payments/initiate-affiliate');
    return response.data;
  },
};

export const supportAPI = {
  submitTalkToSales: async (payload: {
    full_name: string;
    email: string;
    phone?: string;
    company_name?: string;
    website?: string;
    role?: string;
    team_size?: string;
    country?: string;
    requirements: string;
    budget?: string;
    timeline?: string;
  }): Promise<{ message: string }> => {
    const response = await api.post('/support/talk-to-sales', payload);
    return response.data;
  },

  createTicket: async (payload: { title: string; description: string; category?: string; screenshots?: File[] }): Promise<{ message: string; ticket: any }> => {
    const formData = new FormData();
    formData.append('title', payload.title);
    formData.append('description', payload.description);
    if (payload.category) formData.append('category', payload.category);
    (payload.screenshots || []).forEach((file) => {
      formData.append('screenshots', file);
    });
    const response = await api.post('/support/tickets', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  listMyTickets: async (): Promise<{ tickets: any[] }> => {
    const response = await api.get('/support/tickets/mine');
    return response.data;
  },

  listPublishedTickets: async (params: { category?: string } = {}): Promise<{ tickets: any[] }> => {
    const qs = new URLSearchParams();
    if (params.category) qs.append('category', params.category);
    const response = await api.get(`/support/tickets/published${qs.toString() ? `?${qs.toString()}` : ''}`);
    return response.data;
  },

  getTicket: async (ticketId: number): Promise<{ ticket: any }> => {
    const response = await api.get(`/support/tickets/${ticketId}`);
    return response.data;
  },

  withdrawTicket: async (ticketId: number): Promise<{ message: string; ticket: any }> => {
    const response = await api.post(`/support/tickets/${ticketId}/withdraw`);
    return response.data;
  },

  downloadAttachment: async (ticketId: number, attachmentId: number): Promise<Blob> => {
    const response = await api.get(`/support/tickets/${ticketId}/attachments/${attachmentId}/download`, { responseType: 'blob' });
    return response.data;
  },
};

export const notificationsAPI = {
  getNotifications: async (
    filters: { unread_only?: boolean; limit?: number; cursor?: string; since?: string } = {}
  ): Promise<{ items: NotificationItem[]; notifications: NotificationItem[]; next_cursor: string | null; total_unread: number }> => {
    const params = new URLSearchParams();
    if (filters.unread_only) {
      params.append('unread', 'true');
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters.cursor) {
      params.append('cursor', filters.cursor);
    }
    if (filters.since) {
      params.append('since', filters.since);
    }
    const query = params.toString();
    const response = await api.get(`/notifications${query ? `?${query}` : ''}`);
    return response.data;
  },

  getUnreadCount: async (): Promise<{ count: number }> => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  markAllRead: async (filters: { since?: string; type?: string } = {}): Promise<{ updated: number }> => {
    const response = await api.post('/notifications/mark-all-read', filters);
    return response.data;
  },

  markRead: async (notificationId: number): Promise<{ message: string }> => {
    const response = await api.post(`/notifications/${notificationId}/read`);
    return response.data;
  }
};

export interface OrgCapRow {
  domain: string;
  verified_count: number;
  pending_count: number;
  effective_cap: number;
  source: 'global' | 'override';
  is_at_cap: boolean;
  last_alerted_at: string | null;
  verified_count_at_alert: number | null;
  override: {
    id: number;
    domain: string;
    custom_limit: number;
    reason: string | null;
    created_by_admin_id: number;
    updated_by_admin_id: number | null;
    created_at: string | null;
    updated_at: string | null;
  } | null;
}

export interface OrgCapSettings {
  default_limit: number;
  personal_domain_allowlist: string[];
  pending_verification_window_hours: number;
}

export interface OrgAdminRow {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  domain: string;
  is_org_admin: boolean;
  is_active: boolean;
  is_verified: boolean;
  is_deleted: boolean;
  created_at: string | null;
  org_member_count: number;
}

export const orgAdminsAPI = {
  list: async (filters: { domain?: string; search?: string; limit?: number; offset?: number } = {}): Promise<{ items: OrgAdminRow[]; total: number; limit: number; offset: number }> => {
    const params = new URLSearchParams();
    if (filters.domain) params.append('domain', filters.domain);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
    const q = params.toString();
    const response = await api.get(`/admin/billing/org-admins${q ? `?${q}` : ''}`);
    return response.data;
  },
  searchRecruiters: async (q: string): Promise<{ recruiters: OrgAdminRow[] }> => {
    const response = await api.get(`/admin/billing/org-admins/recruiter-search?q=${encodeURIComponent(q)}`);
    return response.data;
  },
  promote: async (recruiterId: number): Promise<OrgAdminRow> => {
    const response = await api.post(`/admin/billing/org-admins/${recruiterId}/promote`);
    return response.data;
  },
  demote: async (recruiterId: number): Promise<OrgAdminRow> => {
    const response = await api.post(`/admin/billing/org-admins/${recruiterId}/demote`);
    return response.data;
  },
};

export interface OrgMember {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: 'candidate' | 'recruiter' | 'admin' | 'partner';
  is_org_admin: boolean;
  is_active: boolean;
  is_verified: boolean;
  is_deleted: boolean;
  last_login: string | null;
  created_at: string | null;
}

export interface OrgInviteRow {
  id: number;
  invited_by_user_id: number;
  invited_domain: string;
  invited_email: string;
  expires_at: string | null;
  used_at: string | null;
  used_user_id: number | null;
  revoked_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  status: 'pending' | 'used' | 'expired' | 'revoked';
  invited_by?: { id: number; first_name: string; last_name: string; email: string };
  used_user?: { id: number; first_name: string; last_name: string; email: string };
}

export interface OrgDashboardPayload {
  org: {
    domain: string;
    members: { recruiters: number; org_admins: number; active: number; suspended: number };
    cap: { effective_cap: number; verified_count: number; is_at_cap: boolean };
  };
  jobs: {
    total: number;
    by_status: Record<string, number>;
    by_review_status: Record<string, number>;
    posted_last_30d: number;
  };
  applications: {
    total: number;
    by_status: Record<string, number>;
    received_last_30d: number;
  };
  top_recruiters_last_90d: Array<{
    user_id: number;
    name: string;
    email: string;
    jobs_posted: number;
    applications_received: number;
    shortlisted: number;
    hired: number;
  }>;
  trend: {
    applications_by_day: Array<{ date: string | null; count: number }>;
    jobs_by_day: Array<{ date: string | null; count: number }>;
  };
}

export interface OrgJobRow extends Job {
  new_applications?: number;
  shortlisted_count?: number;
  hired_count?: number;
  owner?: { id: number; first_name: string; last_name: string; email: string; is_org_admin?: boolean } | null;
}

export const recruiterOrgAPI = {
  listMembers: async (filters: { search?: string; role?: string; status?: string; include_deleted?: boolean; limit?: number; offset?: number } = {}): Promise<{ items: OrgMember[]; total: number; limit: number; offset: number }> => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.role) params.append('role', filters.role);
    if (filters.status) params.append('status', filters.status);
    if (filters.include_deleted) params.append('include_deleted', 'true');
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
    const q = params.toString();
    const response = await api.get(`/recruiter/org/members${q ? `?${q}` : ''}`);
    return response.data;
  },
  suspendMember: async (userId: number, body: { confirm_non_recruiter?: boolean } = {}): Promise<OrgMember> => {
    const response = await api.post(`/recruiter/org/members/${userId}/suspend`, body);
    return response.data;
  },
  resumeMember: async (userId: number): Promise<OrgMember> => {
    const response = await api.post(`/recruiter/org/members/${userId}/resume`);
    return response.data;
  },

  delegateJob: async (jobId: number, payload: { to_user_id: number; reason?: string; confirm_closed?: boolean }): Promise<{ job: OrgJobRow }> => {
    const response = await api.post(`/recruiter/org/jobs/${jobId}/delegate`, payload);
    return response.data;
  },

  listJobs: async (filters: { search?: string; status?: string; review_status?: string; owner_id?: number; page?: number; per_page?: number } = {}): Promise<{ jobs: OrgJobRow[]; total: number; pages: number; current_page: number; per_page: number; has_next: boolean; has_prev: boolean }> => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.review_status) params.append('review_status', filters.review_status);
    if (filters.owner_id !== undefined) params.append('owner_id', filters.owner_id.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    const q = params.toString();
    const response = await api.get(`/recruiter/org/jobs${q ? `?${q}` : ''}`);
    return response.data;
  },

  getDashboard: async (): Promise<OrgDashboardPayload> => {
    const response = await api.get('/recruiter/org/dashboard');
    return response.data;
  },

  createInvite: async (email: string): Promise<OrgInviteRow> => {
    const response = await api.post('/recruiter/org/invites', { email });
    return response.data;
  },
  listInvites: async (filters: { status?: string; limit?: number; offset?: number } = {}): Promise<{ items: OrgInviteRow[]; total: number; limit: number; offset: number }> => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
    const q = params.toString();
    const response = await api.get(`/recruiter/org/invites${q ? `?${q}` : ''}`);
    return response.data;
  },
  revokeInvite: async (inviteId: number): Promise<OrgInviteRow> => {
    const response = await api.post(`/recruiter/org/invites/${inviteId}/revoke`);
    return response.data;
  },
  resendInvite: async (inviteId: number): Promise<OrgInviteRow> => {
    const response = await api.post(`/recruiter/org/invites/${inviteId}/resend`);
    return response.data;
  },
  listAllowlistedPartners: async (): Promise<{
    partners: Array<{ id: number; first_name: string; last_name: string; email: string; kyc_status: string }>
  }> => {
    const response = await api.get('/recruiter/org/allowlisted-partners');
    return response.data;
  },
};

// --- External (imported) Jobs feature --------------------------------------

export interface ExternalJobImportSummary {
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; error: string }>;
  rows_total: number;
}

export const applicationsExternalAPI = {
  redirect: async (jobId: number): Promise<{ source_url: string; application_id: number }> => {
    const response = await api.post('/applications/external-redirect', { job_id: jobId });
    return response.data;
  },
};

export const externalJobsAdminAPI = {
  list: async (filters: {
    page?: number;
    per_page?: number;
    source_name?: string;
    status?: string;
    company_id?: number;
    expired?: 'true' | 'false';
    search?: string;
  } = {}): Promise<{
    jobs: any[];
    total: number;
    pages: number;
    current_page: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  }> => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.source_name) params.append('source_name', filters.source_name);
    if (filters.status) params.append('status', filters.status);
    if (filters.company_id) params.append('company_id', filters.company_id.toString());
    if (filters.expired) params.append('expired', filters.expired);
    if (filters.search) params.append('search', filters.search);
    const q = params.toString();
    const response = await api.get(`/admin/external-jobs${q ? `?${q}` : ''}`);
    return response.data;
  },
  get: async (jobId: number): Promise<{ job: any }> => {
    const response = await api.get(`/admin/external-jobs/${jobId}`);
    return response.data;
  },
  update: async (jobId: number, payload: Record<string, any>): Promise<{ job: any; changed: string[] }> => {
    const response = await api.put(`/admin/external-jobs/${jobId}`, payload);
    return response.data;
  },
  remove: async (jobId: number): Promise<{ job: any }> => {
    const response = await api.delete(`/admin/external-jobs/${jobId}`);
    return response.data;
  },
  listApplications: async (jobId: number, filters: { status?: string; page?: number; per_page?: number } = {}): Promise<{
    applications: any[];
    total: number;
    pages: number;
    current_page: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  }> => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    const q = params.toString();
    const response = await api.get(`/admin/external-jobs/${jobId}/applications${q ? `?${q}` : ''}`);
    return response.data;
  },
  importCsv: async (file: File): Promise<ExternalJobImportSummary> => {
    const fd = new FormData();
    fd.append('file', file);
    const response = await api.post('/admin/external-jobs/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// --- Delegated Jobs feature -------------------------------------------------

export interface OrgPartnerAllowlistRow {
  id: number;
  domain: string;
  partner: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    partner_kyc_status: string | null;
    is_active: boolean;
  } | null;
  added_by_user_id: number;
  added_at: string | null;
  submissions_ever: number;
}

export interface PartnerSearchResult {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  partner_kyc_status: string;
}

export interface DelegatedJobReviewerRow {
  id: number;
  job_id: number;
  recruiter: {
    id: number | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    is_org_admin: boolean;
  };
  added_by_user_id: number;
  added_at: string | null;
}

export type DelegatedSubmissionStatus = 'submitted' | 'shortlisted' | 'rejected' | 'withdrawn';
export type DelegatedSubmissionSource = 'new' | 'existing_user';

export interface DelegatedSubmissionRow {
  id: number;
  job_id: number;
  partner_user_id: number;
  source: DelegatedSubmissionSource;
  candidate_partner_profile_id: number | null;
  candidate_user_id: number | null;
  status: DelegatedSubmissionStatus;
  shortlisted_by_user_id: number | null;
  shortlisted_at: string | null;
  shortlist_notes: string | null;
  rejected_by_user_id: number | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  invited_user_id: number | null;
  invited_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  candidate_preview: {
    first_name: string | null;
    email_masked: string | null;
    current_location: string | null;
    experience_years: number | null;
    experience_months: number | null;
  };
  partner?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    kyc_status: string | null;
  };
  viewed_by_me?: boolean;
  job?: {
    id: number;
    title: string;
    company_id: number | null;
    org_domain: string | null;
    status: string;
  };
}

export interface PartnerDelegatedJobRow extends Job {
  owner_org_domain?: string | null;
  my_submissions_count?: number;
  last_submitted_at?: string | null;
}

export interface RecruiterDelegatedJobRow extends Job {
  submission_count?: number;
  pending_count?: number;
  owner?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    is_org_admin: boolean;
  };
}

export const orgPartnerAllowlistAPI = {
  list: async (filters: { search?: string; limit?: number; offset?: number } = {}): Promise<{ items: OrgPartnerAllowlistRow[]; total: number; limit: number; offset: number }> => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
    const q = params.toString();
    const response = await api.get(`/recruiter/org/partner-allowlist${q ? `?${q}` : ''}`);
    return response.data;
  },
  search: async (q: string): Promise<{ partners: PartnerSearchResult[] }> => {
    const response = await api.get(`/recruiter/org/partner-allowlist/search?q=${encodeURIComponent(q)}`);
    return response.data;
  },
  add: async (partnerUserId: number): Promise<OrgPartnerAllowlistRow> => {
    const response = await api.post('/recruiter/org/partner-allowlist', { partner_user_id: partnerUserId });
    return response.data;
  },
  remove: async (partnerUserId: number): Promise<{ removed_id: number }> => {
    const response = await api.delete(`/recruiter/org/partner-allowlist/${partnerUserId}`);
    return response.data;
  },
};

export const delegationToggleAPI = {
  toggle: async (jobId: number, payload: { is_delegated: boolean; reviewer_user_ids?: number[] }): Promise<{ job: any; reviewers: DelegatedJobReviewerRow[] }> => {
    const response = await api.post(`/recruiter/org/jobs/${jobId}/delegate-toggle`, payload);
    return response.data;
  },
  getReviewers: async (jobId: number): Promise<{ job_id: number; is_delegated: boolean; reviewers: DelegatedJobReviewerRow[] }> => {
    const response = await api.get(`/recruiter/org/jobs/${jobId}/reviewers`);
    return response.data;
  },
  addReviewer: async (jobId: number, recruiterUserId: number): Promise<DelegatedJobReviewerRow> => {
    const response = await api.post(`/recruiter/org/jobs/${jobId}/reviewers`, { recruiter_user_id: recruiterUserId });
    return response.data;
  },
  removeReviewer: async (jobId: number, recruiterUserId: number): Promise<{ removed: boolean }> => {
    const response = await api.delete(`/recruiter/org/jobs/${jobId}/reviewers/${recruiterUserId}`);
    return response.data;
  },
};

export const partnerDelegatedAPI = {
  listJobs: async (filters: { search?: string; location?: string; page?: number; per_page?: number } = {}): Promise<{ jobs: PartnerDelegatedJobRow[]; total: number; pages: number; current_page: number; per_page: number; has_next: boolean; has_prev: boolean }> => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.location) params.append('location', filters.location);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    const q = params.toString();
    const response = await api.get(`/partner/delegated-jobs${q ? `?${q}` : ''}`);
    return response.data;
  },
  getJob: async (jobId: number): Promise<{ job: PartnerDelegatedJobRow }> => {
    const response = await api.get(`/partner/delegated-jobs/${jobId}`);
    return response.data;
  },
  checkEmail: async (
    jobId: number,
    email: string,
    phone?: string,
  ): Promise<{ exists?: boolean; masked?: { first_name: string; email_masked: string }; blocked?: boolean; reason?: string; submission_id?: number }> => {
    const response = await api.post(`/partner/delegated-jobs/${jobId}/check-email`, { email, phone });
    return response.data;
  },
  submitNew: async (jobId: number, formData: FormData): Promise<{ submission: DelegatedSubmissionRow }> => {
    const response = await api.post(`/partner/delegated-jobs/${jobId}/submit`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  shareExisting: async (jobId: number, email: string): Promise<{ submission: DelegatedSubmissionRow }> => {
    const response = await api.post(`/partner/delegated-jobs/${jobId}/share-existing`, { email });
    return response.data;
  },
  mySubmissions: async (filters: { status?: string; job_id?: number; limit?: number; offset?: number } = {}): Promise<{ items: DelegatedSubmissionRow[]; total: number; limit: number; offset: number }> => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.job_id) params.append('job_id', filters.job_id.toString());
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
    const q = params.toString();
    const response = await api.get(`/partner/delegated-jobs/my-submissions${q ? `?${q}` : ''}`);
    return response.data;
  },
  withdraw: async (submissionId: number): Promise<{ submission: DelegatedSubmissionRow }> => {
    const response = await api.post(`/partner/delegated-jobs/submissions/${submissionId}/withdraw`);
    return response.data;
  },
};

export const recruiterDelegatedAPI = {
  hasAccess: async (): Promise<{ has_access: boolean }> => {
    const response = await api.get('/recruiter/delegated-jobs/has-access');
    return response.data;
  },
  listJobs: async (): Promise<{ jobs: RecruiterDelegatedJobRow[]; total: number }> => {
    const response = await api.get('/recruiter/delegated-jobs');
    return response.data;
  },
  listSubmissions: async (jobId: number, filters: { status?: string; source?: string; partner_user_id?: number; limit?: number; offset?: number } = {}): Promise<{ items: DelegatedSubmissionRow[]; total: number; limit: number; offset: number }> => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.source) params.append('source', filters.source);
    if (filters.partner_user_id !== undefined) params.append('partner_user_id', filters.partner_user_id.toString());
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
    const q = params.toString();
    const response = await api.get(`/recruiter/delegated-jobs/${jobId}/submissions${q ? `?${q}` : ''}`);
    return response.data;
  },
  submissionDetail: async (submissionId: number, opts: { confirm?: boolean } = {}): Promise<any> => {
    const q = opts.confirm ? '?confirm=true' : '';
    const response = await api.get(`/recruiter/delegated-jobs/submissions/${submissionId}${q}`);
    return response.data;
  },
  shortlist: async (submissionId: number, notes?: string): Promise<{ submission: DelegatedSubmissionRow }> => {
    const response = await api.post(`/recruiter/delegated-jobs/submissions/${submissionId}/shortlist`, { notes });
    return response.data;
  },
  reject: async (submissionId: number, reason?: string): Promise<{ submission: DelegatedSubmissionRow }> => {
    const response = await api.post(`/recruiter/delegated-jobs/submissions/${submissionId}/reject`, { reason });
    return response.data;
  },
  inviteToPlatform: async (submissionId: number): Promise<{ submission: DelegatedSubmissionRow; candidate_user_id: number }> => {
    const response = await api.post(`/recruiter/delegated-jobs/submissions/${submissionId}/invite-to-platform`);
    return response.data;
  },
  downloadSubmissionResume: async (submissionId: number): Promise<Blob> => {
    const response = await api.get(`/recruiter/delegated-jobs/submissions/${submissionId}/resume`, { responseType: 'blob' });
    return response.data;
  },
};

export interface OrgInvitePreview {
  valid: boolean;
  email?: string;
  inviter_name?: string;
  org_domain?: string;
  expires_at?: string | null;
  reason?: 'expired' | 'used' | 'revoked' | 'not_found';
}

export const authAPIExt = {
  getOrgInvitePreview: async (token: string): Promise<OrgInvitePreview> => {
    const response = await api.get(`/auth/org-invite/${encodeURIComponent(token)}`);
    return response.data;
  },
};

export const orgCapsAPI = {
  list: async (
    filters: { domain?: string; at_cap?: boolean; limit?: number; offset?: number } = {}
  ): Promise<{ items: OrgCapRow[]; total: number; limit: number; offset: number }> => {
    const params = new URLSearchParams();
    if (filters.domain) params.append('domain', filters.domain);
    if (filters.at_cap) params.append('at_cap', 'true');
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
    const query = params.toString();
    const response = await api.get(`/admin/org-caps${query ? `?${query}` : ''}`);
    return response.data;
  },

  upsertOverride: async (payload: { domain: string; custom_limit: number; reason?: string }): Promise<OrgCapRow> => {
    const response = await api.post('/admin/org-caps/overrides', payload);
    return response.data;
  },

  deleteOverride: async (domain: string): Promise<OrgCapRow> => {
    const response = await api.delete(`/admin/org-caps/overrides/${encodeURIComponent(domain)}`);
    return response.data;
  },

  getSettings: async (): Promise<OrgCapSettings> => {
    const response = await api.get('/admin/settings/org-cap');
    return response.data;
  },

  updateSettings: async (payload: Partial<OrgCapSettings>): Promise<OrgCapSettings> => {
    const response = await api.put('/admin/settings/org-cap', payload);
    return response.data;
  },
};

// Skills API (for general use)
export const skillsAPI = {
  getAllSkills: async (): Promise<Skill[]> => {
    const response = await usersAPI.getAllSkills();
    // Flatten the categorized skills into a single array
    return Object.values(response.skills).flat();
  },

  search: async (
    q: string,
  ): Promise<Array<{ id: number; name: string; category?: string; matched_via: 'name' | 'alias' }>> => {
    const response = await api.get('/users/skills', { params: { q } });
    // When ?q= is present, backend returns an array in `skills`.
    return Array.isArray(response.data.skills) ? response.data.skills : [];
  },
};

// Admin Skills API
export const adminSkillsAPI = {
  listPending: async (limit = 25, offset = 0): Promise<{
    pending: Array<{
      id: number; name: string;
      proposed_by: { id: number; email: string; role: string } | null;
      created_at: string;
      usage_count: number;
      similar_approved: Array<{ id: number; name: string; similarity: number }>;
    }>;
    total: number;
  }> => {
    const response = await api.get('/admin/skills/pending', { params: { limit, offset } });
    return response.data;
  },

  pendingCount: async (): Promise<{ count: number }> => {
    const response = await api.get('/admin/skills/pending-count');
    return response.data;
  },

  approve: async (id: number, category?: string): Promise<void> => {
    await api.post(`/admin/skills/${id}/approve`, { category });
  },

  reject: async (id: number, reason?: string): Promise<void> => {
    await api.post(`/admin/skills/${id}/reject`, { reason });
  },

  merge: async (id: number, intoSkillId: number): Promise<void> => {
    await api.post(`/admin/skills/${id}/merge`, { into_skill_id: intoSkillId });
  },

  listApproved: async (limit = 25, offset = 0, q = ''): Promise<{
    approved: Array<{
      id: number;
      name: string;
      category: string | null;
      aliases: Array<{ id: number; alias_name: string }>;
      usage: { job_count: number; user_count: number };
    }>;
    total: number;
  }> => {
    const response = await api.get('/admin/skills/approved', { params: { limit, offset, q } });
    return response.data;
  },
  listRejected: async (limit = 25, offset = 0): Promise<{
    rejected: Array<{
      id: number;
      name: string;
      proposed_by: { id: number; email: string; role: string } | null;
      rejected_at: string | null;
      rejected_by_user_id: number | null;
      rejection_reason: string | null;
      created_at: string | null;
    }>;
    total: number;
  }> => {
    const response = await api.get('/admin/skills/rejected', { params: { limit, offset } });
    return response.data;
  },
  patchSkill: async (id: number, body: { name?: string; category?: string }): Promise<{ skill: any }> => {
    const response = await api.patch(`/admin/skills/${id}`, body);
    return response.data;
  },
  deleteSkill: async (id: number): Promise<{ deleted: boolean; skill_id: number }> => {
    const response = await api.delete(`/admin/skills/${id}`);
    return response.data;
  },
  unreject: async (id: number): Promise<{ skill: any }> => {
    const response = await api.post(`/admin/skills/${id}/unreject`);
    return response.data;
  },
  addAlias: async (id: number, aliasName: string): Promise<{ alias: any }> => {
    const response = await api.post(`/admin/skills/${id}/aliases`, { alias_name: aliasName });
    return response.data;
  },
  deleteAlias: async (id: number, aliasId: number): Promise<{ deleted: boolean; alias_id: number }> => {
    const response = await api.delete(`/admin/skills/${id}/aliases/${aliasId}`);
    return response.data;
  },
  bulkImportAliases: async (rows: Array<{ canonical_name: string; alias_name: string }>): Promise<{
    imported: number;
    skipped: Array<{ row: any; reason: string }>;
    errors: Array<{ row: any; reason: string }>;
  }> => {
    const response = await api.post('/admin/skills/aliases/bulk-import', { rows });
    return response.data;
  },
  healthSummary: async () => {
    const response = await api.get('/admin/skills/health');
    return response.data;
  },
  healthSegment: async (
    segment: 'coverage-gaps' | 'garbage' | 'unused' | 'no-aliases',
    limit = 25,
    offset = 0,
    hideAliasAnchored?: boolean,
  ) => {
    const params: any = { limit, offset };
    if (segment === 'unused' && hideAliasAnchored) params.hide_alias_anchored = 1;
    const response = await api.get(`/admin/skills/health/${segment}`, { params });
    return response.data;
  },
};

export const adminCategoriesAPI = {
  listPending: async (limit = 25, offset = 0, level?: 'parent' | 'child') => {
    const params: any = { limit, offset };
    if (level) params.level = level;
    const response = await api.get('/admin/job-categories/pending', { params });
    return response.data;
  },
  pendingCount: async (): Promise<{ count: number }> => {
    const response = await api.get('/admin/job-categories/pending-count');
    return response.data;
  },
  listApproved: async (limit = 25, offset = 0, q = '', level?: 'parent' | 'child') => {
    const params: any = { limit, offset };
    if (q) params.q = q;
    if (level) params.level = level;
    const response = await api.get('/admin/job-categories/approved', { params });
    return response.data;
  },
  listRejected: async (limit = 25, offset = 0) => {
    const response = await api.get('/admin/job-categories/rejected', { params: { limit, offset } });
    return response.data;
  },
  approve: async (id: number, body: { display_order?: number; also_approve_children?: boolean } = {}) => {
    const response = await api.post(`/admin/job-categories/${id}/approve`, body);
    return response.data;
  },
  reject: async (id: number, reason?: string) => {
    const response = await api.post(`/admin/job-categories/${id}/reject`, { reason });
    return response.data;
  },
  merge: async (id: number, intoCategoryId: number) => {
    const response = await api.post(`/admin/job-categories/${id}/merge`, { into_category_id: intoCategoryId });
    return response.data;
  },
  unreject: async (id: number) => {
    const response = await api.post(`/admin/job-categories/${id}/unreject`);
    return response.data;
  },
  patchCategory: async (id: number, body: { name?: string; display_order?: number; parent_id?: number; description?: string; is_active?: boolean }) => {
    const response = await api.patch(`/admin/job-categories/${id}`, body);
    return response.data;
  },
  deleteCategory: async (id: number) => {
    const response = await api.delete(`/admin/job-categories/${id}`);
    return response.data;
  },
  addAlias: async (id: number, aliasName: string) => {
    const response = await api.post(`/admin/job-categories/${id}/aliases`, { alias_name: aliasName });
    return response.data;
  },
  deleteAlias: async (id: number, aliasId: number) => {
    const response = await api.delete(`/admin/job-categories/${id}/aliases/${aliasId}`);
    return response.data;
  },
  bulkImportAliases: async (rows: Array<{ canonical_name: string; level: 'parent' | 'child'; alias_name: string }>) => {
    const response = await api.post('/admin/job-categories/aliases/bulk-import', { rows });
    return response.data;
  },
};

// Upload API
export const uploadAPI = {
  uploadResume: async (file: File): Promise<{ message: string; filename: string; user: User }> => {
    const formData = new FormData();
    formData.append('resume', file);
    
    const response = await api.post('/uploads/resume', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadResume: async (userId: number): Promise<Blob> => {
    const response = await api.get(`/uploads/resume/${userId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  deleteResume: async (): Promise<{ message: string; user: User }> => {
    const response = await api.delete('/uploads/resume');
    return response.data;
  },

  uploadProfilePicture: async (file: File): Promise<{ message: string; profile: User }> => {
    const formData = new FormData();
    formData.append('profile_picture', file);

    const response = await api.post('/profile/picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  deleteProfilePicture: async (): Promise<{ message: string; profile: User }> => {
    const response = await api.delete('/profile/picture');
    return response.data;
  }
};

// Profile API
export const profileAPI = {
  getProfile: async (): Promise<{ profile: User }> => {
    return usersAPI.getProfile();
  },

  updateProfile: async (profileData: Partial<User>): Promise<{ message: string; profile: User }> => {
    const response = await api.put('/users/profile', profileData);
    return response.data;
  },

  uploadResume: async (file: File): Promise<{ message: string; filename: string; profile: User }> => {
    const formData = new FormData();
    formData.append('resume', file);

    const response = await api.post('/profile/resume', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadDefaultCoverLetter: async (file: File): Promise<{ message: string; profile: User }> => {
    const formData = new FormData();
    formData.append('cover_letter', file);
    const response = await api.post('/profile/cover-letter', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadDefaultCoverLetter: async (): Promise<Blob> => {
    const response = await api.get('/profile/cover-letter', { responseType: 'blob' });
    return response.data;
  },

  deleteDefaultCoverLetter: async (): Promise<{ message: string; profile: User }> => {
    const response = await api.delete('/profile/cover-letter');
    return response.data;
  },

  getProfileHistory: async (page: number = 1, per_page: number = 20): Promise<PaginatedResponse<any> & { history: any[] }> => {
    const response = await api.get(`/profile/history?page=${page}&per_page=${per_page}`);
    return response.data;
  },

  uploadProfilePicture: async (file: File): Promise<{ message: string; profile: User }> => {
    const formData = new FormData();
    formData.append('profile_picture', file);
    const response = await api.post('/profile/picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteProfilePicture: async (): Promise<{ message: string; profile: User }> => {
    const response = await api.delete('/profile/picture');
    return response.data;
  },

  updateAccountStatus: async (is_active: boolean): Promise<{ message: string; profile: User }> => {
    const response = await api.patch('/profile/account/status', { is_active });
    return response.data;
  },

  deleteAccount: async (): Promise<{ message: string; deleted_at?: string }> => {
    const response = await api.delete('/profile/account');
    return response.data;
  },

  getCategoryPreferences: async (): Promise<{
    preferences: CategoryPreferences;
    categories_tree: JobCategory[];
  }> => {
    const response = await api.get('/profile/category-preferences');
    return response.data;
  },

  putCategoryPreferences: async (
    body: { parent_ids: number[]; child_ids: number[] }
  ): Promise<{
    preferences: CategoryPreferences;
    categories_tree: JobCategory[];
  }> => {
    const response = await api.put('/profile/category-preferences', body);
    return response.data;
  }
};

export const recommendationsAPI = {
  getRecommendations: async (): Promise<RecommendationsResponse> => {
    const response = await api.get('/candidate/recommendations');
    return response.data;
  },
};

export const emailPreferencesAPI = {
  getEmailPreferences: async (): Promise<EmailPreferencesResponse> => {
    const response = await api.get('/profile/email-preferences');
    return response.data;
  },
  putEmailPreference: async (
    email_type: string,
    enabled: boolean
  ): Promise<{ preference: EmailPreference }> => {
    const response = await api.put('/profile/email-preferences', { email_type, enabled });
    return response.data;
  },
};

export const privacyPreferencesAPI = {
  getPrivacyPreferences: async (): Promise<PrivacyPreferencesResponse> => {
    const response = await api.get('/profile/privacy-preferences');
    return response.data;
  },
  putPrivacyPreference: async (
    privacy_type: string,
    enabled: boolean
  ): Promise<{ preference: PrivacyPreference }> => {
    const response = await api.put('/profile/privacy-preferences', { privacy_type, enabled });
    return response.data;
  },
};

export const invitationsAPI = {
  getInvitations: async (limit: number = 3): Promise<InvitationsResponse> => {
    const response = await api.get('/candidate/invitations', { params: { limit } });
    return response.data;
  },
  declineInvitation: async (invite_id: number): Promise<{ invitation: RecruiterInvitation }> => {
    const response = await api.put(`/candidate/invitations/${invite_id}/decline`);
    return response.data;
  },
};

export const recruiterMatchingAPI = {
  rank: async (body: RankRequest): Promise<RankResponse> => {
    const response = await api.post('/recruiter/matching/rank', body);
    return response.data;
  },
  createInvite: async (body: CreateInviteRequest): Promise<{ invite: InvitePayload } | { error: string; existing_invite: InvitePayload }> => {
    const response = await api.post('/recruiter/invitations', body);
    return response.data;
  },
};

export default api;
